/**
 * Unmatched-receipt shelf — receipts we HOLD that did not match.
 * Inbound AM review inside Books (feat-unmatched-receipt-review).
 * image_url is rendered as a link only — never fetched server-side.
 *
 * Action outcomes: every click goes through `onShelfAction` so a rejected
 * request cannot stay silent. Success copy stays in each handler — a wrapper
 * that forced one success shape would erase Tag's 409 warnings and the
 * archive/restore distinction the AM needs.
 */
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import {
  AdminApiError,
  RECEIPT_SHELF_CANDIDATE_LIST_LIMIT,
  RECEIPT_SHELF_ELIGIBLE_AFTER_DAYS,
  RECEIPT_SHELF_LIST_LIMIT,
  RECEIPT_SHELF_RESEARCH_LIST_LIMIT,
  receiptShelfCandidateListCapFired,
  receiptShelfListCapFired,
  receiptShelfResearchListCapFired,
  type AdminClient,
} from "../api.js";
import { clear, h, safeText } from "../dom.js";
import { safeReceiptImageHref } from "../receipt_image.js";
import type {
  AdminHubIntakeItem,
  AdminHubIntakeResponse,
  CompanySlug,
  ReceiptShelfArchive,
  ReceiptShelfCandidate,
  ReceiptShelfItem,
  ReceiptShelfWarning,
  StaffRole,
} from "../types.js";

import {
  button,
  disclosure,
  focusAfterWrite,
  neighbourFocusTarget,
  pageHeader,
} from "../ui/components.js";
import {
  HUB_INTAKE_LEDGER_OFF,
  HUB_INTAKE_NOT_SET_UP,
  hubIntakeAmountText,
  hubIntakeAction,
  hubIntakeChip,
  hubIntakeCountsTotal,
  hubIntakeFilterStates,
  hubIntakeFilters,
  hubIntakeSentence,
  type HubIntakeFilterId,
} from "../ui/hub-intake-words.js";
import { dateText, type AccountLabelContext } from "../ui/display-words.js";
import { formatOutPositiveMoney } from "../ui/money.js";
import * as face from "../ui/face.js";
import { failureCopy } from "../ui/failure.js";
import { readerRole } from "../ui/facts.js";
import { navigateTo } from "../ui/nav-bus.js";
import { setReviewScopeHint } from "../ui/review-scope.js";
import { navLabel } from "../ui/sentences.js";
import {
  formatPeriodLabel,
  periodNotAfterUtc,
  shiftPeriod,
  utcPeriod,
} from "../ui/period-control.js";
import { runWrite } from "../ui/write.js";

/**
 * The internal slug as the plain key a wire-shaped helper takes. A Map lookup
 * is the sanctioned slug-to-key boundary: the value is the key itself, never
 * words a person reads.
 */
function slugText(slug: CompanySlug): string {
  return new Map<CompanySlug, string>([[slug, slug]]).get(slug) ?? "";
}

/** `2026-10-07` -> `Oct 7, 2026`; anything unreadable is shown unchanged. */
function formatDisplayDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match === null) return value;
  const date = new Date(`${match[1] ?? ""}-${match[2] ?? ""}-${match[3] ?? ""}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export interface ReceiptShelfPageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  isCurrent?: () => boolean;
  /**
   * Signed-in role, for the shared hold sentences. The shell does not pass it,
   * so the page reads the nav bus (set before any route renders); tests pass it.
   */
  role?: StaffRole;
  /** Clock seam for the current month; the shell uses the wall clock. */
  now?: () => Date;
  /**
   * The shell's company names (slug -> name), from which the page builds its
   * ONE account-label context (rd13 L1): a refusal that names the two sides
   * of a between-companies booking names the companies.
   */
  companyLabels?: ReadonlyMap<CompanySlug, string>;
  /** The shell's ledger segment to company name read (`face.companySegmentNames`, rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
}

/** G11 read: at most 23 months back, 100 rows per read (design §2.9). */
const HUB_INTAKE_MONTHS_BACK = 23;
const HUB_INTAKE_LIMIT = 100;

const HUB_KIND_ORDER: Readonly<Record<string, number>> = {
  receipt: 0,
  bill: 1,
  transfer: 2,
};

/** One empty-shelf sentence: the first paint and the last card's removal. */
const SHELF_EMPTY_COPY =
  "Nothing on the shelf. Receipts younger than the eligibility window are waiting; " +
  "older unmatched ones stay here until tagged, dismissed, or auto-matched. " +
  "Choose Not ours or Duplicates above to see receipts you already filed.";

export async function renderReceiptShelfPage(
  opts: ReceiptShelfPageOptions,
): Promise<void> {
  const { client, company, root } = opts;
  // Owner decision 10: an unknown role is the account manager, never "owner".
  const role: StaffRole = readerRole(opts.role);
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  // The page's ONE label context, built once from the shell's names (rd13 L1).
  const companies = face.accountLabelContext(opts.companyLabels, opts.companySegments);

  // Not after the UTC month: the hub-intake route refuses a later one, so a
  // browser ahead of UTC would show an error on the 1st (rd-x2). Home's hub
  // line uses the same helper, so both count the same month.
  const opened = opts.now?.() ?? new Date();
  const currentPeriod = periodNotAfterUtc(opened);
  // The Worker counts "23 months back" from the UTC month too. Behind UTC (a
  // US evening at month end) the local month minus 23 is a month too old.
  const earliestPeriod = shiftPeriod(utcPeriod(opened), -HUB_INTAKE_MONTHS_BACK);
  let period = currentPeriod;
  let filter: HubIntakeFilterId = "all";
  let fullResponse: AdminHubIntakeResponse | null = null;
  let viewResponse: AdminHubIntakeResponse | null = null;
  let loadToken = 0;

  const hubSection = h("section", {
    class: "hub-intake",
    "data-role": "hub-intake",
  });
  const filterHost = h("div", {
    class: "hub-intake-filters",
    "data-role": "hub-intake-filters",
    role: "group",
    "aria-label": "Filter by status",
  });
  const bodyHost = h("div", {
    class: "hub-intake-body",
    "data-role": "hub-intake-body",
  });

  const monthLabel = h(
    "span",
    { class: "hub-intake-month-label", "data-role": "hub-intake-month" },
    [formatPeriodLabel(period)],
  );
  const prevMonth = button("‹", "quiet", {
    class: "period-chevron hub-intake-chevron",
    "data-action": "hub-intake-prev",
    "aria-label": "Previous month",
  });
  const nextMonth = button("›", "quiet", {
    class: "period-chevron hub-intake-chevron",
    "data-action": "hub-intake-next",
    "aria-label": "Next month",
  });

  const syncMonth = (): void => {
    monthLabel.textContent = formatPeriodLabel(period);
    prevMonth.disabled = period <= earliestPeriod;
    nextMonth.disabled = period >= currentPeriod;
  };
  syncMonth();

  hubSection.append(
    h("header", { class: "hub-intake-header" }, [
      h("div", { class: "hub-intake-heading-block" }, [
        h(
          "h2",
          { class: "hub-intake-heading", "data-role": "hub-intake-heading" },
          [`${navLabel("receipt-shelf")} from the hub`],
        ),
        h("p", { class: "lede", "data-role": "hub-intake-lede" }, [
          "Everything the hub sent this company, and what happened to it.",
        ]),
      ]),
      h("div", { class: "hub-intake-month" }, [
        prevMonth,
        monthLabel,
        nextMonth,
      ]),
    ]),
    filterHost,
    bodyHost,
  );

  const shelfSection = h("section", {
    class: "receipt-shelf-section",
    "data-role": "receipt-shelf-section",
  });

  // A Restore re-draws the PAGE (title, hub list and shelf), never just the
  // section the archive panel sits in.
  const rerenderPage = (): Promise<void> => renderReceiptShelfPage(opts);

  clear(root);
  root.append(receiptsHeader(), hubSection, shelfSection);
  paintHubLoading();
  paintShelfLoading();

  prevMonth.addEventListener("click", () => {
    if (prevMonth.disabled) return;
    period = shiftPeriod(period, -1);
    filter = "all";
    fullResponse = null;
    viewResponse = null;
    // The previous month's chips must not sit beside the new month, or beside
    // an error for it.
    clear(filterHost);
    syncMonth();
    void loadHub(period, "all");
  });
  nextMonth.addEventListener("click", () => {
    if (nextMonth.disabled) return;
    period = shiftPeriod(period, 1);
    filter = "all";
    fullResponse = null;
    viewResponse = null;
    // The previous month's chips must not sit beside the new month, or beside
    // an error for it.
    clear(filterHost);
    syncMonth();
    void loadHub(period, "all");
  });

  // The hub read and the shelf read start together. Each paints its own
  // section, so a slow or failed hub list never hides the shelf (and never
  // takes the page title with it).
  void loadHub(period, "all");

  await loadShelf();

  async function loadShelf(): Promise<void> {
    let shelf: Awaited<ReturnType<AdminClient["listReceiptShelf"]>>;
    try {
      shelf = await client.listReceiptShelf(company);
    } catch (error) {
      if (!stillCurrent()) return;
      // 403 and 503 belong to the shell: receipt-context refusal, workspace
      // denial, and the unavailable retry. Swallowing them hides that split.
      if (
        error instanceof AdminApiError &&
        (error.status === 403 || error.status === 503)
      ) {
        throw error;
      }
      console.error("receipt shelf load failed", error);
      paintShelfError();
      return;
    }
    if (!stillCurrent()) return;
    paintShelf(shelf);
  }

  function paintHubLoading(): void {
    clear(bodyHost);
    bodyHost.append(
      h("p", { class: "muted", "data-role": "hub-intake-loading" }, [
        "Loading…",
      ]),
    );
  }

  function paintShelfLoading(): void {
    clear(shelfSection);
    shelfSection.append(
      receiptShelfHeading(),
      h("p", { class: "muted", "data-role": "receipt-shelf-loading" }, [
        "Loading…",
      ]),
    );
  }

  function paintShelfError(): void {
    clear(shelfSection);
    const retry = button("Try again", "primary", {
      "data-action": "retry",
      "aria-label": "Try again",
    });
    // Re-reads the shelf only: the AM's chosen month and status stay put.
    retry.addEventListener("click", () => {
      paintShelfLoading();
      void loadShelf();
    });
    shelfSection.append(
      receiptShelfHeading(),
      h(
        "div",
        {
          class: "error-state",
          role: "alert",
          "data-role": "receipt-shelf-error",
        },
        [h("p", {}, ["Couldn't load unmatched receipts. Try again."]), retry],
      ),
    );
  }

  function paintShelf(
    shelfBody: Awaited<ReturnType<AdminClient["listReceiptShelf"]>>,
  ): void {
    clear(shelfSection);
    const live = h("div", {
      class: "aria-live",
      "aria-live": "polite",
      "data-role": "receipt-shelf-live",
    });
    shelfSection.append(receiptShelfHeading(), live);

    const archives = h("div", {
      class: "receipt-shelf-archives",
      "data-role": "receipt-shelf-archives",
    });
    // The button's number comes from the shelf read. A dismiss changes it, and
    // leaving it stale made the page say "Not ours: 0" with a receipt just
    // filed (rd round 10, C-2). The write is the one fact that changed, so the
    // count moves with it — no extra read, no full re-render.
    const archiveCounts: Record<ReceiptShelfArchive, number> = {
      not_ours: shelfBody.archives.notOurs,
      duplicate: shelfBody.archives.duplicate,
    };
    const archiveButtons: Partial<Record<ReceiptShelfArchive, HTMLElement>> = {};
    const bumpArchive = (archive: ReceiptShelfArchive): void => {
      archiveCounts[archive] += 1;
      const control = archiveButtons[archive];
      if (control !== undefined) {
        control.textContent = `${archiveLabel(archive)}: ${String(archiveCounts[archive])}`;
        control.setAttribute("data-count", String(archiveCounts[archive]));
      }
      // A panel already open for this archive was drawn from a read older than
      // this dismiss, so it cannot be true any more: it said "Nothing is filed
      // here yet" under a button that now counts one (rd round 10 review,
      // MUST-FIX 2a). Close it; opening it again reads again.
      shelfSection
        .querySelector(
          `[data-role="archive-detail"][data-archive="${archive}"]`,
        )
        ?.remove();
    };
    archiveButtons.not_ours = archiveCountButton(
      archiveLabel("not_ours"),
      () => archiveCounts.not_ours,
      "not_ours",
      company,
      client,
      shelfSection,
      root,
      rerenderPage,
      live,
      stillCurrent,
      role,
    );
    archiveButtons.duplicate = archiveCountButton(
      archiveLabel("duplicate"),
      () => archiveCounts.duplicate,
      "duplicate",
      company,
      client,
      shelfSection,
      root,
      rerenderPage,
      live,
      stillCurrent,
      role,
    );
    archives.append(
      archiveButtons.not_ours,
      archiveButtons.duplicate,
    );
    shelfSection.append(archives);

    if (shelfBody.items.length === 0) {
      shelfSection.append(
        h(
          "p",
          { class: "muted empty-state", "data-role": "receipt-shelf-empty" },
          [SHELF_EMPTY_COPY],
        ),
      );
      return;
    }

    if (receiptShelfListCapFired(shelfBody.items.length)) {
      shelfSection.append(
        h(
          "p",
          {
            class: "banner banner-warn",
            role: "status",
            "data-role": "shelf-list-truncated",
          },
          [
            `Showing ${String(shelfBody.items.length)} receipts — that is the longest list this page can show (${String(RECEIPT_SHELF_LIST_LIMIT)}). More may exist.`,
          ],
        ),
      );
    }

    const list = h("ul", {
      class: "receipt-shelf-list",
      "data-role": "receipt-shelf-list",
    });
    for (const item of shelfBody.items) {
      list.append(
        renderShelfCard(
          item,
          company,
          client,
          stillCurrent,
          live,
          bumpArchive,
          rerenderPage,
          role,
          companies,
        ),
      );
    }
    shelfSection.append(list);
  }

  async function loadHub(
    wantedPeriod: string,
    wantedFilter: HubIntakeFilterId,
  ): Promise<void> {
    const token = ++loadToken;
    paintHubLoading();
    try {
      const loaded = await readHub(wantedPeriod, wantedFilter);
      if (!stillCurrent() || token !== loadToken) return;
      period = wantedPeriod;
      filter = wantedFilter;
      if (wantedFilter === "all") {
        fullResponse = loaded;
      }
      viewResponse = loaded;
      syncMonth();
      paintHub(loaded);
    } catch (error) {
      if (!stillCurrent() || token !== loadToken) return;
      viewResponse = null;
      paintHubError(error);
    }
  }

  async function readHub(
    wantedPeriod: string,
    wantedFilter: HubIntakeFilterId,
  ): Promise<AdminHubIntakeResponse> {
    const states = hubIntakeFilterStates(wantedFilter, role);
    const base = { period: wantedPeriod, limit: HUB_INTAKE_LIMIT };
    if (states === null) return client.listHubIntake(company, base);
    if (states.length === 1) {
      const only = states[0];
      if (only !== undefined) {
        return client.listHubIntake(company, { ...base, state: only });
      }
      throw new Error("hub intake filter has no state");
    }
    // A chip groups states and the route filters one state at a time. Read
    // each state once and merge, so a capped list can still show the rest of
    // this chip rather than pretending the newest 100 are the whole answer.
    const parts = await Promise.all(
      states.map((state) =>
        client.listHubIntake(company, { ...base, state }),
      ),
    );
    const first = parts[0];
    if (first === undefined) {
      throw new Error("hub intake returned no response");
    }
    const items = parts.flatMap((part) => part.items).sort(compareHubItems);
    return {
      ...first,
      total: parts.reduce((sum, part) => sum + part.total, 0),
      capped: parts.some((part) => part.capped),
      countsComplete: parts.every((part) => part.countsComplete),
      items,
    };
  }

  function paintHub(response: AdminHubIntakeResponse): void {
    clear(bodyHost);
    const hasItems =
      response.items.length > 0 || hubIntakeCountsTotal(response.counts) > 0;
    if (response.hubSetup === "not_set_up") {
      // WV2 still lists transfers for a company that cannot receive receipts
      // or bills (a medical company, for one), and a transfer can need a
      // person. Say the sentence, then still show whatever was sent.
      bodyHost.append(notSetUpMessage(hasItems));
      if (!hasItems) {
        clear(filterHost);
        return;
      }
    }

    paintFilters(response);
    if (!response.ledger.available && (hasItems || filter === "booked")) {
      bodyHost.append(
        h(
          "p",
          {
            class: "banner banner-warn",
            role: "status",
            "data-role": "hub-intake-ledger-off",
          },
          [HUB_INTAKE_LEDGER_OFF],
        ),
      );
    }
    if (response.capped) {
      bodyHost.append(
        h(
          "p",
          {
            class: "banner banner-warn",
            role: "status",
            "data-role": "hub-intake-capped",
          },
          [cappedSentence(response.items.length)],
        ),
      );
    }
    if (!response.countsComplete) {
      bodyHost.append(
        h("p", { class: "muted", "data-role": "hub-intake-counts-incomplete" }, [
          "Some older items are not counted. More may exist.",
        ]),
      );
    }

    const states = hubIntakeFilterStates(filter, role);
    const items =
      states === null
        ? response.items
        : response.items.filter((item) => states.includes(item.state));

    if (items.length === 0) {
      const empty = emptyMessage(response);
      if (empty !== null) bodyHost.append(empty);
      return;
    }

    const list = h("ul", {
      class: "hub-intake-list",
      "data-role": "hub-intake-list",
    });
    for (const item of items) {
      list.append(renderHubRow(item, role, company, companies));
    }
    bodyHost.append(list);
  }

  function cappedSentence(shown: number): string {
    if (filter === "all") {
      return `Showing the newest ${String(shown)}. Pick a status to see the rest.`;
    }
    const states = hubIntakeFilterStates(filter, role);
    if (states !== null && states.length === 1) {
      return `Showing the newest ${String(shown)} in this status.`;
    }
    // A grouped chip reads each state on its own, so the merged list is the
    // newest of each, not strictly the newest overall.
    return `Showing ${String(shown)} in this status. Older ones are not shown.`;
  }

  function paintFilters(response: AdminHubIntakeResponse): void {
    clear(filterHost);
    for (const chip of hubIntakeFilters(
      response.counts,
      role,
      response.ledger.available,
      response.countsComplete,
    )) {
      const control = h(
        "button",
        {
          type: "button",
          class: "chip hub-intake-filter",
          "data-role": "hub-intake-filter",
          "data-filter": chip.id,
          "data-active": chip.id === filter ? "true" : "false",
          "aria-pressed": chip.id === filter ? "true" : "false",
          ...(chip.count === null ? {} : { "data-count": String(chip.count) }),
        },
        [chip.count === null ? chip.label : `${chip.label} ${String(chip.count)}`],
      );
      control.addEventListener("click", () => {
        chooseFilter(chip.id);
      });
      filterHost.append(control);
    }
  }

  function chooseFilter(nextFilter: HubIntakeFilterId): void {
    if (nextFilter === filter && viewResponse !== null) return;
    filter = nextFilter;
    if (fullResponse !== null && !fullResponse.capped) {
      viewResponse = fullResponse;
      paintHub(fullResponse);
      return;
    }
    if (nextFilter === "all" && fullResponse !== null) {
      viewResponse = fullResponse;
      paintHub(fullResponse);
      return;
    }
    void loadHub(period, nextFilter);
  }

  function paintHubError(error: unknown): void {
    clear(bodyHost);
    if (fullResponse !== null) {
      paintFilters(fullResponse);
    }
    const retry = button("Try again", "primary", {
      "data-action": "hub-intake-retry",
      "aria-label": "Try again",
    });
    retry.addEventListener("click", () => {
      void loadHub(period, filter);
    });
    bodyHost.append(
      h(
        "div",
        {
          class: "error-state",
          role: "alert",
          "data-role": "hub-intake-error",
        },
        [
          h("p", {}, ["We could not load what the hub sent."]),
          retry,
          disclosure(
            "Show details",
            h(
              "p",
              {},
              [failureCopy(error, { role, context: "read" }).detail ?? "Request failed"],
            ),
          ),
        ],
      ),
    );
  }

  function emptyMessage(response: AdminHubIntakeResponse): HTMLElement | null {
    // "Nothing came in" is a claim about everything the hub sent. When the
    // counts say something is in this view, or they may be short, an empty list
    // is not that claim (round eight sweep).
    const chip = hubIntakeFilters(
      response.counts,
      role,
      response.ledger.available,
      response.countsComplete,
    ).find(
      (candidate) => candidate.id === filter,
    );
    const counted = chip?.count ?? 0;
    if (counted > 0 || !response.countsComplete) {
      if (filter === "booked" && !response.ledger.available) return null;
      if (response.hubSetup === "not_set_up" && filter === "all") return null;
      return h(
        "p",
        { class: "muted empty-state", "data-role": "hub-intake-empty-uncertain" },
        ["Nothing is listed here, but not everything could be counted. Try again later."],
      );
    }
    if (filter !== "all") {
      // While the books copy is off the Booked list is empty because nothing
      // can be shown as booked, not because nothing was booked: the banner
      // above already says so, and "Nothing in this status." would not.
      if (filter === "booked" && !response.ledger.available) return null;
      return h(
        "p",
        { class: "muted empty-state", "data-role": "hub-intake-empty" },
        ["Nothing in this status."],
      );
    }
    if (response.hubSetup === "not_set_up") return null;
    const text =
      period === currentPeriod
        ? "Nothing has come in from the hub yet."
        : `Nothing came in from the hub in ${formatPeriodLabel(period)}.`;
    return h(
      "p",
      { class: "muted empty-state", "data-role": "hub-intake-empty" },
      [text],
    );
  }
}

function compareHubItems(a: AdminHubIntakeItem, b: AdminHubIntakeItem): number {
  if (a.date !== b.date) return a.date < b.date ? 1 : -1;
  if (a.receivedAt !== b.receivedAt) {
    return a.receivedAt < b.receivedAt ? 1 : -1;
  }
  const kindA = HUB_KIND_ORDER[a.kind] ?? 9;
  const kindB = HUB_KIND_ORDER[b.kind] ?? 9;
  if (kindA !== kindB) return kindA - kindB;
  return b.id - a.id;
}

/**
 * One sentence, no instruction: a medical company can never be set up for
 * receipts or bills, so "ask the owner to set it up" would be untrue there.
 */
function notSetUpMessage(listBelow: boolean): HTMLElement {
  return h(
    "p",
    {
      class: listBelow ? "muted" : "muted empty-state",
      "data-role": listBelow ? "hub-intake-not-set-up" : "hub-intake-empty",
      "data-kind": "not-set-up",
    },
    [HUB_INTAKE_NOT_SET_UP],
  );
}

/** Shown for an amount that cannot be read. Never an empty cell. */
const UNREADABLE_AMOUNT = "\u2014";

/**
 * One amount, one spelling and one sign (rd Y3, rd L2): money, never `89.5` or
 * `42.00 USD`, in the sign Activity, Books and To review use (money out is
 * negative). The hub stores receipts and bills the other way round, so the one
 * shared helper flips it. An amount the contract cannot read shows a dash,
 * never a blank cell (money.ts's never-silently-blank invariant).
 */
function hubAmount(item: AdminHubIntakeItem): string {
  return hubIntakeAmountText(item);
}

/**
 * One amount, one spelling and one sign (rd Y3, rd L2): money, never `89.5` or
 * `42.00 USD`, in the sign Activity, Books and To review use (money out is
 * negative). A shelf receipt is hub data and a candidate row is the bank feed's
 * own line — both are stored money-out-positive, so both go through
 * money.ts's one helper (rd L2 review round 1). An amount the contract cannot
 * read shows a dash, never a blank cell (money.ts's never-silently-blank
 * invariant).
 */
function shelfAmount(amount: string): string {
  return formatOutPositiveMoney(amount, UNREADABLE_AMOUNT);
}

function hubWhat(item: AdminHubIntakeItem): string {
  if (item.kind === "transfer") {
    switch (item.transferKind) {
      case "loan_to":
        return "Loan to another company";
      case "loan_from":
        return "Loan from another company";
      case "capital_contribution":
        return "Money put in by the owner";
      case "owner_distribution":
        return "Money taken out by the owner";
      case "intercompany_transfer":
        return "Transfer between your companies";
      case "loan_repayment":
        return "Loan repayment";
      default:
        return "Transfer";
    }
  }
  const vendor =
    item.vendor === undefined
      ? ""
      : safeVisibleText(item.vendor);
  if (vendor !== "") return vendor;
  return item.kind === "receipt" ? "Receipt" : "Bill";
}

function safeVisibleText(value: string): string {
  return amVisibleDisplayText(value).trim();
}

function formatShortDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match === null) return safeVisibleText(value);
  const date = new Date(
    `${match[1] ?? ""}-${match[2] ?? ""}-${match[3] ?? ""}T00:00:00Z`,
  );
  if (Number.isNaN(date.getTime())) return safeVisibleText(value);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function methodLabel(method: AdminHubIntakeItem["method"]): string | null {
  switch (method) {
    case "moov":
      // One word everywhere for a Moov payment (rd polish rules §1).
      return "Wallet payment";
    case "eft":
      return "Bank transfer";
    case "check":
      return "Check";
    case "card":
      return "Card";
    case "other":
      return "Other";
    default:
      return null;
  }
}

function stateChipClass(state: AdminHubIntakeItem["state"]): string {
  if (state === "booked") return "chip-ok";
  if (
    state === "waiting_on_you" ||
    state === "with_the_owner" ||
    state === "held"
  ) {
    return "chip-warn";
  }
  return "chip-info";
}

function renderHubRow(
  item: AdminHubIntakeItem,
  role: StaffRole,
  company: CompanySlug,
  companies: AccountLabelContext,
): HTMLElement {
  const row = h("li", {
    class: "hub-intake-row",
    "data-role": "hub-intake-row",
    "data-state": item.state,
    "data-kind": item.kind,
  });

  const cardLast4 =
    item.cardLast4 !== undefined && /^[0-9]{4}$/.test(item.cardLast4)
      ? item.cardLast4
      : null;
  const hubAccount =
    item.hubAccount === undefined ? "" : safeVisibleText(item.hubAccount);
  const property =
    item.property === undefined ? "" : safeVisibleText(item.property);

  row.append(
    h("div", { class: "hub-intake-main" }, [
      h("span", { class: "hub-intake-date" }, [dateText(item.date) || formatShortDate(item.date)]),
      h("span", { class: "hub-intake-what" }, [hubWhat(item)]),
      h("span", { class: "hub-intake-amount" }, [hubAmount(item)]),
      h(
        "span",
        {
          class: `chip ${stateChipClass(item.state)}`,
          "data-role": "hub-intake-chip",
          "data-state": item.state,
        },
        [hubIntakeChip(item, role)],
      ),
    ]),
    h(
      "p",
      { class: "hub-intake-sentence", "data-role": "hub-intake-sentence" },
      [hubIntakeSentence(item, role, companies)],
    ),
  );

  if (item.state === "booked" && hubAccount !== "") {
    row.append(
      h(
        "p",
        { class: "hub-intake-hub-said", "data-role": "hub-intake-hub-said" },
        [`Hub said: ${hubAccount}`],
      ),
    );
  }

  const action = hubIntakeAction(item, role);
  if (action !== null) {
    const control = button(action.label, "secondary", {
      "data-action":
        action.kind === "books"
          ? "hub-intake-books"
          : action.kind === "review"
            ? "hub-intake-review"
            : "hub-intake-shelf",
    });
    if (action.kind === "books") {
      control.addEventListener("click", () => {
        // The month the entry is in; never the bank reference id in the
        // visible Books search box (rd polish rules §1, R7-20).
        navigateTo("general-ledger", { period: action.period });
      });
    } else if (action.kind === "review") {
      control.addEventListener("click", () => {
        // Hand To review the company, the month and the exact card this row
        // names (`queueId`), so the person lands on it instead of the top of
        // the whole fleet list (rd L2).
        const period = item.date.slice(0, 7);
        setReviewScopeHint({
          company,
          month: /^\d{4}-(0[1-9]|1[0-2])$/.test(period) ? period : null,
          openCardRef:
            item.queueId === undefined
              ? null
              : { source: "queue", refId: String(item.queueId) },
        });
        navigateTo("queue");
      });
    } else {
      control.addEventListener("click", () => {
        const target = document.querySelector(
          '[data-role="receipt-shelf-section"]',
        );
        if (
          target instanceof HTMLElement &&
          typeof target.scrollIntoView === "function"
        ) {
          target.scrollIntoView({ block: "start" });
        }
      });
    }
    row.append(control);
  }

  const details: HTMLElement[] = [];
  if (cardLast4 !== null) {
    details.push(h("p", {}, [`Card ending ${cardLast4}`]));
  }
  if (property !== "") {
    details.push(h("p", {}, [`Property: ${property}`]));
  }
  const method = methodLabel(item.method);
  if (method !== null) {
    details.push(h("p", {}, [`Method: ${method}`]));
  }
  // The row face already carries the hub date; the bank line's own date goes
  // here, because the two can sit in different months.
  if (item.bankLine !== undefined && item.bankLine.date.trim() !== "") {
    details.push(
      h("p", {}, [
        `Bank: ${safeVisibleText(formatDisplayDate(item.bankLine.date))}`,
      ]),
    );
  }
  if (details.length > 0) {
    row.append(disclosure("Show details", h("div", {}, details)));
  }

  return row;
}

function receiptsHeader(): HTMLElement {
  const header = pageHeader(
    navLabel("receipt-shelf"),
    "Everything the hub sent by month, and receipts that still have no bank line.",
  );
  header.querySelector("h1")?.setAttribute("data-role", "receipt-shelf-title");
  header.querySelector("p")?.setAttribute("data-role", "receipts-page-lede");
  return header;
}

function receiptShelfHeading(): HTMLElement {
  return h("div", { class: "receipt-shelf-heading-block" }, [
    h(
      "h2",
      { class: "receipt-shelf-heading", "data-role": "receipt-shelf-heading" },
      ["Receipts with no bank line yet"],
    ),
    h("p", { class: "lede", "data-role": "receipt-shelf-lede" }, [
      `Receipts we already hold that did not match a bank transaction. ` +
        `A receipt appears here ${String(RECEIPT_SHELF_ELIGIBLE_AFTER_DAYS)} days after its receipt date. ` +
        `A later bank post can clear a card with no action from you. When it does not, use Show candidates, Research other transactions, or Dismiss.`,
    ]),
  ]);
}

/**
 * Bind a click to async work that always reports failure into `live`.
 * Success messaging stays with the caller so each action keeps its own voice.
 * Returns whether work completed without throwing (and the page is still current).
 *
 * Failure copy is one plain sentence plus Try again. The server line, including
 * the old "failed:" prefix, is behind Show details. Home still uses
 * `runAnnouncedAction` directly.
 *
 * A 404 or 409 on a receipt action means the receipt is no longer in the state
 * the button assumes (already dismissed, not unmatched, not in an archive, not
 * available for tagging). That request can never succeed on a retry, so the
 * caller's `onAlreadyHandled` says what happened and re-reads instead of
 * inviting "Try again" (R7-19). Network and 5xx failures keep "Try again".
 */
export async function runShelfAction(
  live: HTMLElement,
  stillCurrent: () => boolean,
  _actionLabel: string,
  work: () => Promise<void>,
  onAlreadyHandled?: (error: AdminApiError) => Promise<void> | void,
  /**
   * A write (Tag) says the one shared failed-save sentence, including "Ask the
   * owner." for an account manager's 403. A read (an archive load) keeps its
   * own "Couldn't complete …" wording, because nothing was saved.
   */
  writeFailureCopy?: boolean,
  role?: StaffRole,
): Promise<boolean> {
  const reader = readerRole(role);
  try {
    await work();
    return stillCurrent();
  } catch (error) {
    if (!stillCurrent()) return false;
    if (
      onAlreadyHandled !== undefined &&
      error instanceof AdminApiError &&
      (error.status === 404 || error.status === 409)
    ) {
      await onAlreadyHandled(error);
      return false;
    }
    // One shared sentence on the face; the server's words stay behind Show
    // details (rd round 12, A5-2). A write reads as a write, a read as a read.
    const copy = failureCopy(error, {
      role: reader,
      context: writeFailureCopy === true ? "write" : "read",
    });
    clear(live);
    live.append(document.createTextNode(copy.sentence));
    if (copy.detail !== null) {
      live.append(disclosure("Show details", h("p", {}, [copy.detail])));
    }
    return false;
  }
}

/** The one sentence for a receipt the server says is already handled. */
function announceAlreadyHandled(
  live: HTMLElement,
  error: AdminApiError,
  role: StaffRole,
): void {
  announcePlain(
    live,
    "That receipt was already handled.",
    failureCopy(error, { role, context: "write" }).detail ?? "",
  );
}

/**
 * After the last card leaves, say the shelf is empty instead of leaving a bare
 * list (R7-22b). The list is the page's own; the archives above stay.
 */
function noteShelfEmptyIfLast(list: HTMLElement | null): void {
  if (list === null) return;
  if (list.querySelector("[data-receipt-id]") !== null) return;
  const section = list.closest('[data-role="receipt-shelf-section"]');
  if (section === null) return;
  if (section.querySelector('[data-role="receipt-shelf-empty"]') !== null) return;
  list.replaceWith(
    h("p", { class: "muted empty-state", "data-role": "receipt-shelf-empty" }, [
      SHELF_EMPTY_COPY,
    ]),
  );
}

function announcePlain(live: HTMLElement, sentence: string, detail: string): void {
  clear(live);
  live.append(document.createTextNode(sentence));
  live.append(disclosure("Show details", h("p", {}, [detail])));
}

/**
 * Controls an action has deliberately left off — a refused Tag button must not
 * be offered again — which the press guard below must not switch back on.
 */
const KEEP_OFF = new WeakSet<HTMLElement>();

function keepOff(control: HTMLElement): void {
  KEEP_OFF.add(control);
}

/**
 * One press, one request (rd round 10, A9-M3).
 *
 * A double click on a save or a dismiss used to send the same write twice: the
 * second answer could be a 404 ("nothing to dismiss") painted over a message
 * that said the work failed, or a second group, tag or setup record. So the
 * pressed control and the other buttons beside it are disabled for the whole
 * action and a second click does nothing. Everything the action did not
 * deliberately leave off is switched back on afterwards, including on failure,
 * so a person can try again.
 *
 * `beside` is the row of buttons the pressed one sits in (the card's actions,
 * the archive pair, an editor's Save/Cancel). A control that was already
 * disabled stays disabled: a period chevron at the end of its range must not be
 * re-enabled by an unrelated action finishing. `scope` replaces that row when
 * the actions that must not overlap are not siblings (the opening-equity card's
 * Confirm, Reject and Correct live in different containers of one card).
 *
 * A press that had focus gets it back when the action ends: disabling the
 * pressed control takes focus off it, and leaving it on <body> loses the
 * person's place (rd round 10 review, MUST-FIX 4c). Focus is restored only when
 * nobody else has taken it, and never to a control that left the page.
 */
export function runOnce(
  control: HTMLButtonElement,
  work: () => Promise<unknown>,
  scope?: HTMLElement,
): void {
  if (control.disabled) return;
  const hadFocus = document.activeElement === control;
  const row = scope ?? control.parentElement;
  const beside =
    row === null
      ? []
      : Array.from(row.querySelectorAll<HTMLButtonElement>("button")).filter(
          (other) => other !== control && !other.disabled,
        );
  control.disabled = true;
  for (const other of beside) other.disabled = true;
  void (async () => {
    try {
      await work();
    } catch (error) {
      // The work reports its own outcome (each action keeps its own voice).
      // The guard's whole job is to put the controls back.
      console.error("action failed after the press was guarded", error);
    } finally {
      if (!KEEP_OFF.has(control)) control.disabled = false;
      for (const other of beside) {
        if (!KEEP_OFF.has(other)) other.disabled = false;
      }
      const active = document.activeElement;
      if (
        hadFocus &&
        control.isConnected &&
        !control.disabled &&
        (active === null || active === document.body)
      ) {
        control.focus();
      }
    }
  })();
}

function onShelfAction(
  button: HTMLButtonElement,
  live: HTMLElement,
  stillCurrent: () => boolean,
  actionLabel: string,
  work: () => Promise<void>,
  onAlreadyHandled?: (error: AdminApiError) => Promise<void> | void,
  writeFailureCopy?: boolean,
  role?: StaffRole,
): void {
  button.addEventListener("click", () => {
    runOnce(button, () =>
      runShelfAction(
        live,
        stillCurrent,
        actionLabel,
        work,
        onAlreadyHandled,
        writeFailureCopy,
        role,
      ),
    );
  });
}

function archiveLabel(archive: ReceiptShelfArchive): string {
  return archive === "not_ours" ? "Not ours" : "Duplicates";
}

function archiveCountButton(
  label: string,
  count: () => number,
  archive: ReceiptShelfArchive,
  company: CompanySlug,
  client: AdminClient,
  host: HTMLElement,
  pageRoot: HTMLElement,
  rerender: () => Promise<void>,
  live: HTMLElement,
  stillCurrent: () => boolean,
  role: StaffRole,
): HTMLButtonElement {
  const btn = button(`${label}: ${String(count())}`, "quiet", {
    class: "linkish",
    "data-role": `archive-${archive}`,
    "data-count": String(count()),
  });
  onShelfAction(btn, live, stillCurrent, `Load ${label} archive`, async () => {
    // Drop a stale panel before the request so a failed switch cannot leave
    // the AM reading the previous archive under a new click belief.
    host.querySelector('[data-role="archive-detail"]')?.remove();
    const body = await client.listReceiptShelfArchive(company, archive);
    if (!stillCurrent()) return;
    const panel = h("section", {
      class: "receipt-shelf-archive-detail",
      "data-role": "archive-detail",
      "data-archive": archive,
    });
    // Both numbers come from the count the button shows NOW, read at open
    // time: a dismiss raises it after the page was drawn, and a panel drawn
    // from the draw-time number printed the old total (button 51, heading 50)
    // and took the wrong empty branch (rd round 10 review, MUST-FIX 2b). A
    // count that has not caught up must never hide a receipt the server just
    // handed us, so it is raised to what the read returned.
    const filed = Math.max(count(), body.items.length);
    panel.append(h("h2", { "data-role": "archive-heading" }, [
      `${label} (${String(filed)})`,
    ]));
    if (body.items.length === 0) {
      if (count() > 0) {
        // The button counted some and the list came back with none: that is a
        // list that did not load, not an empty archive.
        panel.append(
          h("p", { class: "muted", role: "status", "data-role": "archive-unlisted" }, [
            `${String(count())} filed here, but the list came back empty. Try again.`,
          ]),
        );
      } else {
        panel.append(
          h("p", { class: "muted empty-state", "data-role": "archive-empty" }, [
            "Nothing is filed here yet. Dismiss a receipt and it will show in this list.",
          ]),
        );
      }
    } else {
      if (body.items.length < count()) {
        panel.append(
          h(
            "p",
            {
              class: "banner banner-warn",
              role: "status",
              "data-role": "archive-truncated",
            },
            [
              `Showing ${String(body.items.length)} of ${String(count())} — older dismissals are not listed.`,
            ],
          ),
        );
      }
      const ul = h("ul", {});
      for (const item of body.items) {
        const li = h("li", {}, [
          safeText(
            `${formatDisplayDate(item.receiptDate)} · ${shelfAmount(item.amountDecimal)} · ${amVisibleDisplayText(item.vendor)}`,
          ),
        ]);
        const restore = button("Restore to shelf", "secondary", {
          "data-role": "restore",
        });
        onShelfAction(
          restore,
          live,
          stillCurrent,
          "Restore",
          async () => {
            const outcome = await runWrite({
              key: `restore:${String(item.id)}`,
              scope: li,
              gate: () => null,
              send: () => client.restoreReceiptShelfItem(company, item.id),
              stillCurrent,
            });
            if (outcome.kind !== "landed") {
              throw outcome.error instanceof Error ? outcome.error : new Error("The restore did not land.");
            }
            if (!stillCurrent()) return;
            await rerender();
            if (!stillCurrent()) return;
            const nextLive = pageRoot.querySelector(
              '[data-role="receipt-shelf-live"]',
            );
            if (nextLive instanceof HTMLElement) {
              announcePlain(
                nextLive,
                "Restored to the shelf.",
                `Receipt ${String(item.id)}`,
              );
            }
          },
          async (error) => {
            // Already restored elsewhere: re-read instead of offering a retry
            // that can never succeed (R7-19).
            await rerender();
            if (!stillCurrent()) return;
            const nextLive = pageRoot.querySelector(
              '[data-role="receipt-shelf-live"]',
            );
            if (nextLive instanceof HTMLElement) {
              announceAlreadyHandled(nextLive, error, role);
            }
          },
          true,
          role,
        );
        li.append(restore);
        ul.append(li);
      }
      panel.append(ul);
    }
    host.append(panel);
  }, undefined, undefined, role);
  return btn;
}

function renderShelfCard(
  item: ReceiptShelfItem,
  company: CompanySlug,
  client: AdminClient,
  stillCurrent: () => boolean,
  live: HTMLElement,
  onDismissed: (archive: ReceiptShelfArchive) => void,
  rerenderPage: () => Promise<void>,
  role: StaffRole,
  companies: AccountLabelContext,
): HTMLElement {
  const li = h("li", {
    class: "receipt-shelf-card card",
    "data-receipt-id": String(item.id),
  });

  // Anchoring: show receipt values; never pre-select a candidate.
  const facts = h("div", { class: "receipt-facts" }, [
    h("strong", {}, [safeText(amVisibleDisplayText(item.vendor))]),
    h("span", {}, [
      safeText(
        `${formatDisplayDate(item.receiptDate)} · ${shelfAmount(item.amountDecimal)}`,
      ),
    ]),
    h("span", {}, [safeText(`Property: ${amVisibleDisplayText(item.property)}`)]),
    h("span", {}, [safeText(`Location: ${amVisibleDisplayText(item.location)}`)]),
    h("span", {}, [safeText(`Submitted by: ${amVisibleDisplayText(item.submittedBy)}`)]),
  ]);
  // The hub's own account name, as sent. Untrusted text: safeText only.
  if (item.glAccount !== undefined) {
    facts.append(
      h("span", { "data-role": "shelf-hub-account" }, [
        safeText(`Hub account: ${amVisibleDisplayText(item.glAccount)}`),
      ]),
    );
  }
  if (item.glParent !== undefined) {
    facts.append(
      h("span", { "data-role": "shelf-hub-parent-account" }, [
        safeText(`Hub parent account: ${amVisibleDisplayText(item.glParent)}`),
      ]),
    );
  }
  li.append(facts);

  const href = safeReceiptImageHref(item.imageUrl, slugText(company));
  if (href !== null) {
    li.append(
      h(
        "a",
        {
          href,
          target: "_blank",
          rel: "noopener noreferrer",
          "data-role": "receipt-image-link",
        },
        ["Open receipt image"],
      ),
    );
  }

  const actions = h("div", { class: "receipt-shelf-actions" });
  const showCandidates = button("Show candidates", "primary", {
    "data-role": "show-candidates",
  });
  const research = button("Research other transactions", "secondary", {
    "data-role": "research",
  });
  const dismissNotOurs = button("Dismiss — not ours", "quiet", {
    "data-role": "dismiss-not-ours",
  });
  const dismissDup = button("Dismiss — duplicate", "quiet", {
    "data-role": "dismiss-duplicate",
  });
  actions.append(showCandidates, research, dismissNotOurs, dismissDup);
  li.append(actions);

  const detail = h("div", {
    class: "receipt-shelf-detail",
    "data-role": "detail",
  });
  li.append(detail);

  onShelfAction(
    showCandidates,
    live,
    stillCurrent,
    "Show candidates",
    async () => {
      const body = await client.listReceiptShelfCandidates(company, item.id);
      if (!stillCurrent()) return;
      clear(detail);
      detail.append(h("p", { class: "muted" }, [
        "Candidates on this card (±3 days). Nothing is pre-selected.",
      ]));
      detail.append(
        renderCandidateList(
          body.candidates,
          item,
          company,
          client,
          stillCurrent,
          live,
          li,
          { searchKind: "card" },
          role,
          companies,
        ),
      );
    },
    undefined,
    undefined,
    role,
  );

  research.addEventListener("click", () => {
    clear(detail);
    const input = h("input", {
      type: "search",
      placeholder: "Search company transactions",
      "data-role": "research-q",
    });
    const go = button("Search", "primary", { "data-role": "research-go" });
    const results = h("div", { "data-role": "research-results" });
    detail.append(input, go, results);
    onShelfAction(go, live, stillCurrent, "Research", async () => {
      const body = await client.searchReceiptShelfTransactions(
        company,
        input.value,
      );
      if (!stillCurrent()) return;
      clear(results);
      results.append(
        renderCandidateList(
          body.items,
          item,
          company,
          client,
          stillCurrent,
          live,
          li,
          { searchKind: "research" },
          role,
          companies,
        ),
      );
    },
    undefined,
    undefined,
    role,
  );
  });

  // Registers click handlers on the dismiss buttons (does not fire dismiss).
  const wireDismiss = (archive: ReceiptShelfArchive): void => {
    onShelfAction(
      archive === "not_ours" ? dismissNotOurs : dismissDup,
      live,
      stillCurrent,
      "Dismiss",
      async () => {
        const outcome = await runWrite({
          key: `dismiss:${String(item.id)}:${archive}`,
          scope: li,
          gate: () => null,
          send: () => client.dismissReceiptShelfItem(company, item.id, archive),
          stillCurrent,
        });
        if (outcome.kind !== "landed") {
          throw outcome.error instanceof Error ? outcome.error : new Error("The dismiss did not land.");
        }
        if (!stillCurrent()) return;
        announcePlain(
          live,
          archive === "not_ours" ? "Dismissed as not ours." : "Dismissed as a duplicate.",
          `Receipt ${String(item.id)}`,
        );
        // The archive's count moves with the receipt that just joined it, so
        // opening it right away shows this receipt rather than "Nothing is
        // filed here yet" (rd round 10, C-2).
        onDismissed(archive);
        // The row that had focus is about to leave: carry focus to the next row
        // (or the one before it), else to the announcement, never to <body>.
        const next = neighbourFocusTarget(li);
        const shelfList = li.parentElement;
        li.remove();
        noteShelfEmptyIfLast(shelfList);
        focusAfterWrite(next ?? live);
      },
      async (error) => {
        // Already dismissed elsewhere: re-read so the page shows the receipt's
        // real state, and say it was handled rather than invite a retry.
        await rerenderPage();
        if (!stillCurrent()) return;
        const nextLive = live.ownerDocument.querySelector(
          '[data-role="receipt-shelf-live"]',
        );
        if (nextLive instanceof HTMLElement) {
          announceAlreadyHandled(nextLive, error, role);
        }
      },
      true,
      role,
    );
  };
  wireDismiss("not_ours");
  wireDismiss("duplicate");

  return li;
}

function renderCandidateList(
  candidates: ReceiptShelfCandidate[],
  receipt: ReceiptShelfItem,
  company: CompanySlug,
  client: AdminClient,
  stillCurrent: () => boolean,
  live: HTMLElement,
  card: HTMLElement,
  opts: { searchKind: "card" | "research" },
  role: StaffRole,
  companies: AccountLabelContext,
): HTMLElement {
  const wrap = h("div", { "data-role": "candidate-list-wrap" });
  const ul = h("ul", { class: "receipt-shelf-candidates" });

  if (candidates.length === 0) {
    if (
      opts.searchKind === "card" &&
      (receipt.cardLast4 === null || receipt.cardLast4 === undefined)
    ) {
      // Searched-nothing vs found-nothing: card-based query never ran.
      ul.append(
        h(
          "li",
          { class: "muted", "data-role": "candidates-not-searched" },
          [
            "This receipt carries no card number, so the card-based candidate search does not apply. Use Research to search by vendor or amount.",
          ],
        ),
      );
    } else {
      ul.append(
        h("li", { class: "muted empty-state", "data-role": "candidates-empty" }, [
          "No candidates. Try Research to search by vendor or amount.",
        ]),
      );
    }
    wrap.append(ul);
    return wrap;
  }

  if (
    opts.searchKind === "card" &&
    receiptShelfCandidateListCapFired(candidates.length)
  ) {
    wrap.append(
      h(
        "p",
        {
          class: "banner banner-warn",
          role: "status",
          "data-role": "candidates-truncated",
        },
        [
          `Showing ${String(candidates.length)} possible matches — that is the longest list this page can show (${String(RECEIPT_SHELF_CANDIDATE_LIST_LIMIT)}). More may exist.`,
        ],
      ),
    );
  }
  if (
    opts.searchKind === "research" &&
    receiptShelfResearchListCapFired(candidates.length)
  ) {
    wrap.append(
      h(
        "p",
        {
          class: "banner banner-warn",
          role: "status",
          "data-role": "research-truncated",
        },
        [
          `Showing ${String(candidates.length)} results — that is the longest list this page can show (${String(RECEIPT_SHELF_RESEARCH_LIST_LIMIT)}). Refine the search for the rest.`,
        ],
      ),
    );
  }

  for (const c of candidates) {
    const li = h("li", {});
    // Receipt values beside each candidate (anchoring).
    li.append(
      h("div", {}, [
        safeText(
          `Transaction ${formatDisplayDate(c.txnDate)} · ${shelfAmount(c.amountDecimal)}` +
            (c.accountLast4 ? ` · card ending ${amVisibleDisplayText(c.accountLast4)}` : "") +
            (c.merchantName ? ` · ${amVisibleDisplayText(c.merchantName)}` : ""),
        ),
      ]),
      h("div", { class: "muted" }, [
        safeText(
          `Receipt was ${formatDisplayDate(receipt.receiptDate)} · ${shelfAmount(receipt.amountDecimal)} · ${amVisibleDisplayText(receipt.vendor)}`,
        ),
      ]),
    );
    if (c.alreadyHasReceipt) {
      li.append(
        h("p", { class: "banner banner-warn" }, [
          "A receipt already exists for this transaction",
        ]),
      );
    }
    const tag = button("Tag", "primary", { "data-role": "tag" });
    onShelfAction(
      tag,
      live,
      stillCurrent,
      "Tag",
      async () => {
        const result: {
          value: Awaited<ReturnType<AdminClient["tagReceiptShelfItem"]>> | null;
        } = { value: null };
        const outcome = await runWrite({
          key: `tag:${String(receipt.id)}:${c.plaidTransactionId}`,
          scope: li,
          gate: () => null,
          send: () =>
            client.tagReceiptShelfItem(company, receipt.id, c.plaidTransactionId),
          afterLanded: (value) => {
            result.value = value;
          },
          stillCurrent,
        });
        if (outcome.kind !== "landed") {
          throw outcome.error instanceof Error ? outcome.error : new Error("The tag did not land.");
        }
        const taggedResult = result.value;
        if (!stillCurrent() || taggedResult === null) return;
        if (!taggedResult.tagged) {
          // The refusal is the Worker's answer to this receipt and this
          // transaction, so the same request is refused again every time. Say
          // what is refused and what to do instead, and take the button away
          // rather than invite a repeat (re-check M2).
          tag.disabled = true;
          // Taken away for good: the same request is refused again every time.
          // The press guard must not hand it back when the action ends.
          keepOff(tag);
          const sentence = showTagRefusal(li, taggedResult.warnings, companies);
          announcePlain(live, sentence, "Tag blocked.");
          return;
        }
        announcePlain(live, "Tagged this receipt.", `Receipt ${String(receipt.id)}`);
        const shelfList = card.parentElement;
        card.remove();
        noteShelfEmptyIfLast(shelfList);
      },
      (error) => {
        // Only two answers mean this receipt itself is handled elsewhere: it
        // is gone, or it can no longer be tagged. Those leave the shelf and
        // say so; "Try again" would be a dead end (R7-19).
        const serverMessage = (
          failureCopy(error, { role, context: "write" }).detail ?? ""
        ).toLowerCase();
        const receiptIsHandled =
          serverMessage.includes("receipt not found") ||
          serverMessage.includes("not available for tagging");
        if (receiptIsHandled) {
          const shelfList = card.parentElement;
          card.remove();
          noteShelfEmptyIfLast(shelfList);
          announceAlreadyHandled(live, error, role);
          // The button that had focus left the page: carry focus to the
          // sentence, the way Dismiss does (round-2 review, item 12).
          focusAfterWrite(live);
          return;
        }
        // Any other 404/409 is about this candidate transaction, not the
        // receipt: another receipt can still be tagged, so the card and its
        // candidates stay and the person is told to pick another. Removing the
        // card here told them the receipt was handled when it was not
        // (round-2 review, item 2). The row's own Tag button goes off for good:
        // the same transaction is gone, so the same request can only fail
        // again (round-3 review, item 5).
        tag.disabled = true;
        keepOff(tag);
        announcePlain(
          live,
          "That transaction is no longer available. Pick another.",
          failureCopy(error, { role, context: "write" }).detail ?? "",
        );
        focusAfterWrite(live);
      },
      true,
      role,
    );
    li.append(tag);
    ul.append(li);
  }
  wrap.append(ul);
  return wrap;
}

const TAG_REFUSAL_COPY: Record<ReceiptShelfWarning["kind"], string> = {
  card_property_mismatch: `This bank line is on another company's card, so it cannot be tagged here. Book it between companies from ${navLabel("queue")}, or choose a different transaction.`,
  already_has_receipt:
    "Another receipt is already on this bank line. Choose a different transaction, or leave this receipt where it is.",
};

/**
 * One block per row, replaced on every refusal: a second refusal never adds a
 * second copy under the first. Returns the sentence for the live region.
 */
export function showTagRefusal(
  row: HTMLElement,
  warnings: readonly ReceiptShelfWarning[],
  companies: AccountLabelContext,
): string {
  row.querySelector('[data-role="tag-refusal"]')?.remove();
  const kinds = [...new Set(warnings.map((warning) => warning.kind))];
  const block = h("div", { class: "banner banner-warn", "data-role": "tag-refusal" });
  for (const kind of kinds) {
    const detail = warnings
      .filter((warning) => warning.kind === kind)
      .map((warning) => renderWarningDetail(warning, companies));
    block.append(
      h("div", { "data-role": "tag-refusal-line" }, [
        h("p", {}, [TAG_REFUSAL_COPY[kind]]),
        disclosure("Show details", h("div", {}, detail)),
      ]),
    );
  }
  row.append(block);
  return kinds.map((kind) => TAG_REFUSAL_COPY[kind]).join(" ");
}

function renderWarningDetail(
  w: ReceiptShelfWarning,
  companies: AccountLabelContext,
): HTMLElement {
  const detail = h("div", {});
  detail.append(h("p", {}, [safeText(amVisibleDisplayText(w.message))]));
  if (w.kind === "card_property_mismatch") {
    // Account paths are words here, never colon paths (rd round 12, A4).
    const between = `Booking between companies uses ${face.account(
      w.intercompany.dueFromAccount,
      companies,
    )} and ${face.account(w.intercompany.dueToAccount, companies)}.`;
    detail.append(h("p", {}, [safeText(between)]));
  }
  return detail;
}
